import React, { useEffect, useState } from "react";
import rsfservice from "../../../services/rsfservice";
import { Badge, Offcanvas, Placeholder, Stack, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleInfo,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import DateRangePicker from "../../../common/DateRangePicker";
import Swal from "sweetalert2";
import MoneyBadge from "./common/MoneyBadge";

function Settlements() {
  const [rsfData, setRsfData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [activeData, setActiveData] = useState(null);

  const handleClose = () => {
    setActiveData(null);
    setShow(false);
  };
  const handleShow = (item) => {
    setActiveData(item);
    setShow(true);
  };

  const handleSettleTransaction = async (item) => {
    try {
      const data = await rsfservice.settleTransaction({
        date: new Date(item.created_at).toISOString().split("T")[0], // Format the date correctly
        counter_np_id: item.receiver,
      });
      console.log("====================================");
      console.log(data);
      console.log("====================================");

      // Show success message with SweetAlert2
      Swal.fire({
        icon: "success",
        title: "Transaction Settled",
        text: data.message, // Display the message from the response
        confirmButtonText: "OK",
      });
    } catch (error) {
      // Show error message with SweetAlert2
      Swal.fire({
        icon: "error",
        title: "Transaction Failed",
        text:
          error.response?.data?.message ||
          "An error occurred while settling the transaction.",
        confirmButtonText: "OK",
      });
    }
  };

  // Fetch RSF data with optional filters
  const fetchRsfData = async (startDate = null, endDate = null) => {
    setLoading(true);
    try {
      let url = null;
      if (startDate && endDate) {
        url = `?startDate=${startDate}&endDate=${endDate}`;
      }
      const response = await rsfservice.getRsf(url);

      setRsfData(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch RSF data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRsfData();
  }, []);

  const DataTable = ({ data }) => {
    if (loading) {
      return (
        <Placeholder as="div" animation="glow">
          {Array.from({ length: 10 }).map((_, index) => (
            <Placeholder key={index} xs={12} className="mb-2" />
          ))}
        </Placeholder>
      );
    }

    if (!data || data.length === 0) {
      return <p className="text-center">No data available</p>;
    }

    return (
      <Table
        striped
        bordered
        hover
        responsive
        size="sm"
        style={{ fontSize: "14px" }}
      >
        <thead>
          <tr>
            <th>#</th>
            <th>Created At</th>
            <th>Amount</th>
            <th>Collector Amount</th>
            <th>Receiver</th>
            <th>GST</th>
            <th>Receiver Amount</th>
            <th>Domain</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={item._id}
              className={item._id === activeData?._id ? "table-primary" : ""}
            >
              <td>{index + 1}</td>
              <td>
                <Badge>{new Date(item.created_at).toLocaleDateString()}</Badge>
              </td>
              <td>
                <MoneyBadge price={item.amount} />
              </td>
              <td>
                <MoneyBadge price={item.collector_amount} />
              </td>
              <td>{item.receiver}</td>
              <td>
                <MoneyBadge price={item.gst} />
              </td>
              <td>
                <MoneyBadge price={item.receiver_amount} />
              </td>
              <td>{item.domain}</td>
              <td>{item.status}</td>
              <td className="align-items-center">
                <FontAwesomeIcon
                  color="yellow"
                  className="me-2"
                  icon={faEdit}
                  style={{ cursor: "pointer" }}
                />
                <FontAwesomeIcon
                  color="green"
                  className="me-2"
                  icon={faCircleCheck}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSettleTransaction(item)}
                />
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  onClick={() => handleShow(item)}
                  style={{ cursor: "pointer" }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Handle date range filter
  const handleDateFilter = (startDate, endDate) => {
    fetchRsfData(startDate, endDate);
  };

  return (
    <div>
      <Stack gap={3}>
        <DateRangePicker getFilteredDate={handleDateFilter} />
        <DataTable data={rsfData.length ? rsfData[0]?.data : []} />
      </Stack>

      {show && (
        <Offcanvas show={show} onHide={handleClose} className="w-50">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Transaction Details</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <h5 className="mb-4">Details of the Selected Transaction</h5>
            <Table
              striped
              bordered
              hover
              responsive
              size="sm"
              style={{ fontSize: "14px" }}
            >
              <tbody>
                <tr>
                  <th>ID</th>
                  <td>{activeData._id}</td>
                </tr>
                <tr>
                  <th>Amount</th>
                  <td>
                    <Badge bg="success" style={{ fontSize: "12px" }}>
                      &#8377;{activeData.amount}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <th>Collector</th>
                  <td>{activeData.collector}</td>
                </tr>
                <tr>
                  <th>Collector Amount</th>
                  <td>
                    <Badge
                      bg="warning"
                      style={{ fontSize: "12px", color: "black" }}
                    >
                      &#8377;{activeData.collector_amount}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <th>Collector URI</th>
                  <td>
                    <a
                      href={activeData.collector_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {activeData.collector_uri}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>Created At</th>
                  <td>
                    <Badge>
                      {new Date(activeData.created_at).toLocaleString()}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <th>Currency</th>
                  <td>{activeData.currency}</td>
                </tr>
                <tr>
                  <th>Domain</th>
                  <td>{activeData.domain}</td>
                </tr>
                <tr>
                  <th>GST</th>
                  <td>{activeData.gst}</td>
                </tr>
                <tr>
                  <th>Message ID</th>
                  <td>{activeData.message_id}</td>
                </tr>
                <tr>
                  <th>Order ID</th>
                  <td>{activeData.order_id}</td>
                </tr>
                <tr>
                  <th>Receiver</th>
                  <td>{activeData.receiver}</td>
                </tr>
                <tr>
                  <th>Receiver Amount</th>
                  <td>&#8377;{activeData.receiver_amount}</td>
                </tr>
                <tr>
                  <th>Receiver URI</th>
                  <td>
                    <a
                      href={activeData.receiver_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {activeData.receiver_uri}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>Self Amount</th>
                  <td>&#8377;{activeData.self_amount}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>{activeData.status}</td>
                </tr>
                <tr>
                  <th>TCS</th>
                  <td>&#8377;{activeData.tcs}</td>
                </tr>
                <tr>
                  <th>TDS</th>
                  <td>&#8377;{activeData.tds}</td>
                </tr>
                <tr>
                  <th>Transaction ID</th>
                  <td>{activeData.transaction_id}</td>
                </tr>
              </tbody>
            </Table>
          </Offcanvas.Body>
        </Offcanvas>
      )}
    </div>
  );
}

export default Settlements;
